import React, { useState, useEffect } from "react";
import { Dialog, Button, Field, SimpleSelectItem, Avatar, Combobox, Checkbox, Alert } from "@/components/ui";
import { extractInitials, useSocialCoachSession, useTiktokPreferences } from "@/utils";
import { Info, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onClose: () => void;
  userId?: string | number;
  isPhotoPost?: boolean;
}

export function TiktokPreferences({ userId, isPhotoPost = false, open, onClose }: Props) {
  const { userId: sessionUserId } = useSocialCoachSession();
  const effectiveUserId = userId || sessionUserId;

  const { preferences, isLoading, isUpdating, updatePreferencesAsync, isError, error } =
    useTiktokPreferences(effectiveUserId);

  // Privacy & Interactions
  const [privacyLevel, setPrivacyLevel] = useState<SimpleSelectItem | null>(null);
  const [privacyOptions, setPrivacyOptions] = useState<SimpleSelectItem[]>([]);
  const [allowComments, setAllowComments] = useState<boolean>(false);
  const [allowDuets, setAllowDuets] = useState<boolean>(false);
  const [allowStitching, setAllowStitching] = useState<boolean>(false);

  // Commercial Content Disclosure
  const [commercialToggle, setCommercialToggle] = useState<boolean>(false);
  const [isYourBrand, setIsYourBrand] = useState<boolean>(false);
  const [isBrandedContent, setIsBrandedContent] = useState<boolean>(false);

  const creatorInfo = preferences?.creatorInfo;
  const commentDisabled = creatorInfo?.commentDisabled ?? false;

  const duetDisabled = isPhotoPost || (creatorInfo?.duetDisabled ?? true);
  const stitchDisabled = isPhotoPost || (creatorInfo?.stitchDisabled ?? true);

  const nickName = creatorInfo?.creatorNickname ?? "";
  const avatarUrl = creatorInfo?.creatorAvatarUrl ?? "";
  const canMakePosts = creatorInfo?.canMakePosts ?? true;

  const selfOnly = privacyLevel?.value === "SELF_ONLY";

  useEffect(() => {
    if (preferences) {
      let options: SimpleSelectItem[] = [
        { value: "PUBLIC_TO_EVERYONE", label: "Public to Everyone" },
        { value: "MUTUAL_FOLLOW_FRIENDS", label: "Mutual Follow Friends" },
        { value: "SELF_ONLY", label: "Self Only" },
      ];
      const availableOptions = preferences.creatorInfo?.privacyLevelOptions;
      if (availableOptions && availableOptions.length > 0) {
        options = options.filter(o => o.value && availableOptions.includes(o.value));
      }
      setPrivacyOptions(options);

      const initialLevel = preferences.id ? options.find(o => o.value === preferences.privacyLevel) ?? null : null;
      setPrivacyLevel(initialLevel);

      // Enforce false default unless explicitly saved as true
      setAllowComments(preferences.allowComments ?? false);
      setAllowDuets(preferences.allowDuets ?? false);
      setAllowStitching(preferences.allowStitching ?? false);

      setCommercialToggle(preferences.brandedContentDisclosure ?? false);
      setIsYourBrand(preferences.brandedContentType === "FIRST_PARTY" || preferences.brandedContentType == "BOTH");
      setIsBrandedContent(preferences.brandedContentType === "THIRD_PARTY" || preferences.brandedContentType == "BOTH");
    }
  }, [preferences]);

  // Handle Privacy / Branded Content collision (Req 3b)
  useEffect(() => {
    if (selfOnly) {
      setIsBrandedContent(false);
    }
  }, [selfOnly]);

  useEffect(() => {
    if (isError && open) {
      toast.error(error?.message || "Failed to load TikTok preferences.");
    }
  }, [isError, error, open]);

  const saveTiktokPreferences = async (): Promise<void> => {
    try {
      let determinedType: string | null = null;
      if (!commercialToggle) determinedType = null;
      else if (isYourBrand && isBrandedContent) determinedType = "BOTH";
      else if (isYourBrand) determinedType = "FIRST_PARTY";
      else if (isBrandedContent) determinedType = "THIRD_PARTY";
      await updatePreferencesAsync({
        allowComments,
        allowDuets,
        allowStitching,
        brandedContentDisclosure: commercialToggle,
        brandedContentType: determinedType,
        privacyLevel: privacyLevel?.value || "PUBLIC_TO_EVERYONE",
      });

      onClose();
    } catch (error: any) {
      console.error("Failed to update TikTok preferences", error);
      toast.error(error?.message || "Failed to save TikTok preferences. Please try again.");
    }
  };

  const allowSettings = [
    {
      label: "Comment",
      key: "comments",
      updateAction: setAllowComments,
      value: allowComments,
      disabled: commentDisabled,
      visible: true,
    },
    {
      label: "Duet",
      key: "duets",
      updateAction: setAllowDuets,
      value: allowDuets,
      disabled: duetDisabled,
      visible: !isPhotoPost,
    },
    {
      label: "Stitch",
      key: "stitch",
      updateAction: setAllowStitching,
      value: allowStitching,
      disabled: stitchDisabled,
      visible: !isPhotoPost,
    },
  ].filter(setting => setting.visible);

  // Validation
  const isCommercialInvalid = commercialToggle && !isYourBrand && !isBrandedContent;
  const isSaveDisabled = !privacyLevel || isLoading || !canMakePosts || isCommercialInvalid;

  // Dynamic Content Labels & Declarations
  const getCommercialContentLabel = () => {
    if (isBrandedContent) return "Your photo/video will be labeled as 'Paid partnership'";
    if (isYourBrand) return "Your photo/video will be labeled as 'Promotional content'";
    return null;
  };

  const commercialLabel = getCommercialContentLabel();

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Header>
        <Dialog.Title>TikTok Posting Preferences</Dialog.Title>
        <Dialog.Description>
          Select the privacy level and commercial settings for your TikTok account.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="px-0 py-0 max-h-[50vh] overflow-y-auto">
        <Field.Set className={"px-4"}>
          {isLoading ? (
            <div className="flex justify-center p-4 gap-2">
              <Loader2 className="animate-spin text-slate-400 mb-2" size={24} />
              Loading preferences...
            </div>
          ) : (
            <>
              {!canMakePosts && (
                <Alert variant="destructive" className="mb-4">
                  <Info className="h-4 w-4 shrink-0 mt-[2px] opacity-80" />
                  <div className="text-md leading-relaxed tracking-tight">
                    This account cannot make more posts at this time. Please try again later.
                  </div>
                </Alert>
              )}

              <Field>
                <Field.Label>Tiktok Account</Field.Label>
                <Field.Content>
                  <div className="flex flex-row gap-2 items-center">
                    <Avatar>
                      <Avatar.Image src={avatarUrl} />
                      <Avatar.Fallback>{extractInitials(nickName)}</Avatar.Fallback>
                    </Avatar>
                    <div className="font-semibold">{nickName}</div>
                  </div>
                </Field.Content>
              </Field>

              <Field.Group>
                <Field>
                  <Field.Label>Privacy Setting</Field.Label>
                  <Combobox
                    value={privacyLevel}
                    items={privacyOptions}
                    itemToStringValue={i => i.value || "None"}
                    itemToStringLabel={i => String(i.label ?? "")}
                    placeholder={"Select a privacy setting (required)"}
                    onValueChange={i => setPrivacyLevel(i)}
                    required={true}
                  />
                </Field>

                {/* Updated Checkboxes for Allow Settings */}

                <Field.Group>
                  <Field.Label>Allow users to</Field.Label>
                  <div className="grid grid-cols-3 gap-4">
                    {allowSettings.map(option => (
                      <Field
                        orientation="horizontal"
                        key={`tiktok-option-${option.key}`}
                        className="flex items-center gap-2"
                      >
                        <Checkbox
                          id={`tiktok-option-${option.key}`}
                          checked={option.value}
                          disabled={option.disabled}
                          onCheckedChange={v => option.updateAction(v)}
                        />
                        <Field.Label
                          htmlFor={`tiktok-option-${option.key}`}
                          className={`cursor-pointer font-normal ${option.disabled ? "opacity-50" : ""}`}
                        >
                          {option.label}
                        </Field.Label>
                      </Field>
                    ))}
                  </div>
                </Field.Group>
              </Field.Group>

              {/* Commercial Content Section */}
              <Field.Group>
                <Field.Label>Content Disclosure Setting</Field.Label>
                <Field orientation="horizontal">
                  <Checkbox
                    id="commercialToggle"
                    checked={commercialToggle}
                    disabled={isUpdating}
                    onCheckedChange={setCommercialToggle}
                  />
                  <Field.Label htmlFor="commercialToggle" className="cursor-pointer font-normal">
                    This content promotes yourself, a brand, product, or service.
                  </Field.Label>
                </Field>

                {commercialToggle && (
                  <div className="flex flex-col gap-3 pl-6 mt-2 border-l-2 border-slate-100">
                    <Field orientation="horizontal">
                      <Checkbox
                        id="yourBrand"
                        checked={isYourBrand}
                        onCheckedChange={checked => setIsYourBrand(checked)}
                      />
                      <Field.Label htmlFor="yourBrand" className="cursor-pointer font-normal">
                        Your brand (Promoting yourself or your own business)
                      </Field.Label>
                    </Field>

                    <Field orientation="horizontal">
                      <div
                        className="flex items-center group"
                        title={selfOnly ? "Branded content visibility cannot be set to private." : ""}
                      >
                        <Checkbox
                          id="brandedContent"
                          checked={isBrandedContent}
                          disabled={selfOnly}
                          onCheckedChange={checked => setIsBrandedContent(checked)}
                        />
                        <Field.Label
                          htmlFor="brandedContent"
                          className={`cursor-pointer font-normal ml-2 ${selfOnly ? "opacity-50" : ""}`}
                        >
                          Branded content (Promoting another brand or third party)
                        </Field.Label>
                      </div>
                    </Field>

                    {commercialLabel && <div className="text-sm text-slate-500 italic mt-1">{commercialLabel}</div>}

                    {isCommercialInvalid && (
                      <div className="text-sm text-red-500 font-medium mt-1">
                        You need to indicate if your content promotes yourself, a third party, or both.
                      </div>
                    )}
                  </div>
                )}
              </Field.Group>
            </>
          )}
        </Field.Set>
      </Dialog.Content>

      <Dialog.Footer className="flex-col items-start sm:flex-col sm:items-start gap-4">
        <div className="text-xs text-slate-500 w-full text-center sm:text-left">
          By posting, you agree to TikTok's{" "}
          {isBrandedContent && (
            <a
              href="https://www.tiktok.com/legal/branded-content-policy"
              target="_blank"
              rel="noreferrer"
              className="underline text-xs hover:text-slate-800"
            >
              Branded Content Policy
            </a>
          )}
          {isBrandedContent && " and "}
          <a
            href="https://www.tiktok.com/legal/music-usage-confirmation"
            target="_blank"
            rel="noreferrer"
            className="underline text-xs hover:text-slate-800"
          >
            Music Usage Confirmation
          </a>
          .
        </div>

        <div className="w-full flex justify-end">
          <Button loading={isUpdating} disabled={isSaveDisabled} onClick={saveTiktokPreferences}>
            Save
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
}
